import {
    Autocomplete,
    FormControlLabel,
    MenuItem,
    Stack,
    Switch,
    TextField,
    Tooltip,
} from "@mui/material";

import { Controller } from "react-hook-form";
import { camelCaseToTitleCase } from "../../../utils";

function SingleTextField({ input, label, disabled = false, ...otherProps }) {
    const isNumber = ["float", "int"].includes(input.type);
    return (
        <TextField
            label={label}
            type={isNumber ? "number" : "text"}
            select={Boolean(input.choices)}
            disabled={disabled}
            fullWidth
            slotProps={
                isNumber
                    ? {
                          htmlInput: {
                              step: input.type === "int" ? 1 : "any",
                          },
                      }
                    : undefined
            }
            {...otherProps}
        >
            {input.choices
                ? input.choices.map((choice) => (
                      <MenuItem key={choice} value={choice}>
                          {choice}
                      </MenuItem>
                  ))
                : null}
        </TextField>
    );
}

function ControlledSingleTextField({ name, input, label, disabled }) {
    const isPath = ["file", "folder"].includes(input.format);
    return (
        <Controller
            name={name}
            disabled={disabled}
            render={({
                field: {
                    ref,
                    value,
                    onChange,
                    disabled: formDisabled,
                    ...field
                },
            }) => (
                <SingleTextField
                    input={input}
                    label={label}
                    {...field}
                    disabled={formDisabled || isPath}
                    inputRef={ref}
                    value={value ?? ""}
                    onChange={(e) => {
                        if (e.target.value === "") return onChange(null);
                        if (
                            !input.choices &&
                            ["float", "int"].includes(input.type)
                        )
                            return onChange(e.target.valueAsNumber);
                        return onChange(e.target.value);
                    }}
                />
            )}
        />
    );
}

function BooleanSwitch({ name, label, disabled }) {
    return (
        <Controller
            name={name}
            disabled={disabled}
            render={({ field: { ref, value, onChange, ...field } }) => (
                <FormControlLabel
                    label={label}
                    control={
                        <Switch
                            {...field}
                            inputRef={ref}
                            checked={value ?? false}
                            onChange={(e) => onChange(e.target.checked)}
                        />
                    }
                    labelPlacement="start"
                    sx={{
                        mx: 1,
                        width: "100%",
                        justifyContent: "space-between",
                    }}
                />
            )}
        />
    );
}

function MultiValueAutocomplete({ name, input, label, disabled }) {
    const isNumber = ["float", "int"].includes(input.type);
    const isPath = ["file", "folder"].includes(input.format);
    return (
        <Controller
            name={name}
            disabled={disabled}
            render={({
                field: {
                    ref,
                    name: fieldName,
                    value,
                    onChange,
                    onBlur,
                    disabled: formDisabled,
                },
            }) => (
                <Autocomplete
                    multiple
                    freeSolo={!input.choices}
                    options={input.choices ?? []}
                    getOptionLabel={(option) => String(option)}
                    value={value ?? []}
                    onBlur={onBlur}
                    onChange={(_, newValue) => {
                        if (newValue.length === 0) return onChange(null);
                        if (!input.choices && isNumber) {
                            return onChange(newValue.map(Number));
                        }
                        return onChange(newValue);
                    }}
                    disabled={formDisabled || isPath}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            name={fieldName}
                            inputRef={ref}
                            label={label}
                            type={isNumber ? "number" : "text"}
                            slotProps={
                                isNumber
                                    ? {
                                          htmlInput: {
                                              ...params.inputProps,
                                              step:
                                                  input.type === "int"
                                                      ? 1
                                                      : "any",
                                          },
                                      }
                                    : undefined
                            }
                        />
                    )}
                    fullWidth
                />
            )}
        />
    );
}

function SwitchTextField({ name, input, label, disabled }) {
    const isPath = ["file", "folder"].includes(input.format);
    return (
        <Controller
            name={name}
            disabled={disabled}
            render={({
                field: {
                    ref,
                    name: fieldName,
                    value,
                    onChange,
                    onBlur,
                    disabled: formDisabled,
                },
            }) => {
                const enabled = value != null;
                // value `null` = flag absent; `true` = flag present, no
                // value; any other value = flag present with that value.
                const textValue = value == null || value === true ? "" : value;
                return (
                    <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        sx={{ ml: 1 }}
                    >
                        <FormControlLabel
                            label={label}
                            disabled={formDisabled || isPath}
                            control={
                                <Switch
                                    checked={enabled}
                                    onBlur={onBlur}
                                    onChange={(e) =>
                                        onChange(e.target.checked ? true : null)
                                    }
                                />
                            }
                            labelPlacement="start"
                            slotProps={{
                                typography: { sx: { whiteSpace: "nowrap" } },
                            }}
                        />
                        <SingleTextField
                            input={input}
                            label="Value (optional)"
                            disabled={!enabled || formDisabled || isPath}
                            name={fieldName}
                            inputRef={ref}
                            onBlur={onBlur}
                            value={textValue}
                            onChange={(e) => {
                                if (e.target.value === "")
                                    return onChange(true);
                                if (
                                    !input.choices &&
                                    ["float", "int"].includes(input.type)
                                )
                                    return onChange(e.target.valueAsNumber);
                                return onChange(e.target.value);
                            }}
                        />
                    </Stack>
                );
            }}
        />
    );
}

/**
 * @param {string} jobName
 * @param {{
 *   key: string,
 *   position: number|null,
 *   type: "string"|"float"|"int"|"boolean",
 *   format: null|"folder"|"file",
 *   nargs: number|"+"|"?"|null,
 *   choices: null|string[]|number[],
 *   argName: string,
 *   description: string,
 * }} input
 * @param {boolean} [disabled] - render the control read-only
 */
export function ModuleInput({ jobName, input, disabled }) {
    const label =
        input.position != null
            ? camelCaseToTitleCase(input.key)
            : input.argName;

    let control;
    if (input.type === "boolean") {
        control = (
            <BooleanSwitch
                name={`${jobName}.main.${input.key}`}
                label={label}
                disabled={disabled}
            />
        );
    } else if (input.nargs === "+") {
        control = (
            <MultiValueAutocomplete
                name={`${jobName}.main.${input.key}`}
                input={input}
                label={label}
                disabled={disabled}
            />
        );
    } else if (typeof input.nargs === "number") {
        control = (
            <Stack direction="row" spacing={1} sx={{ "& > *": { flex: 1 } }}>
                {Array.from({ length: input.nargs }).map((_, i) => (
                    <ControlledSingleTextField
                        key={i}
                        name={`${jobName}.main.${input.key}.${i}`}
                        input={input}
                        label={`${label} (${i + 1})`}
                        disabled={disabled}
                    />
                ))}
            </Stack>
        );
    } else if (input.nargs === "?" && input.position == null) {
        control = (
            <SwitchTextField
                name={`${jobName}.main.${input.key}`}
                input={input}
                label={label}
                disabled={disabled}
            />
        );
    } else {
        control = (
            <ControlledSingleTextField
                name={`${jobName}.main.${input.key}`}
                input={input}
                label={label}
                disabled={disabled}
            />
        );
    }

    return (
        <Tooltip title={input.description} placement="right" arrow>
            <span>{control}</span>
        </Tooltip>
    );
}
